import { z } from "zod";
import type { EntityPatchOp } from "./types";

const entityRefSchema = z.union([z.string(), z.object({ $: z.string() })]);
const attrPathSchema = z.array(z.string()).readonly();

const jsonValueSchema: z.ZodType<unknown> = z.lazy(() =>
  z.union([
    z.string(),
    z.number(),
    z.boolean(),
    z.null(),
    z.array(jsonValueSchema),
    z.record(jsonValueSchema),
  ])
);

const jsonObjectSchema = z.record(jsonValueSchema);

export const EntityPatchOpSchema: z.ZodType<EntityPatchOp> =
  z.discriminatedUnion("op", [
    z.object({
      op: z.literal("set"),
      entity: entityRefSchema,
      path: attrPathSchema,
      value: jsonValueSchema,
      reason: z.string().optional(),
    }),
    z.object({
      op: z.literal("merge"),
      entity: entityRefSchema,
      path: attrPathSchema,
      value: jsonObjectSchema,
      reason: z.string().optional(),
    }),
    z.object({
      op: z.literal("delete"),
      entity: entityRefSchema,
      path: attrPathSchema,
      reason: z.string().optional(),
    }),
    z.object({
      op: z.literal("increment"),
      entity: entityRefSchema,
      path: attrPathSchema,
      delta: z.number(),
      reason: z.string().optional(),
    }),
    // LAF-19 — incremental compose-profile op. No `path`: the op appends to
    // `lineage` + additively installs the profile's marker component + feature
    // resource. `profileId` is the compendium profile id to compose.
    z.object({
      op: z.literal("compose-profile"),
      entity: entityRefSchema,
      profileId: z.string(),
      reason: z.string().optional(),
    }),
    // Spell-anchor slice 2 — decompose-profile op (general inverse of
    // compose-profile). No `path`: removes the recorded owned-keys for
    // `profileId`, strips it from lineage, and prunes the ledger entry.
    z.object({
      op: z.literal("decompose-profile"),
      entity: entityRefSchema,
      profileId: z.string(),
      reason: z.string().optional(),
    }),
    // LAF-335 — modify-resource op. No `path`: commits a resource via
    // setResource (dual-writes state.resources working-copy + the persisted
    // resource_<id> component), shallow-merging `patch` over the existing
    // resource so handlers/monitors/base survive. Forge/level-up HP consume it.
    z.object({
      op: z.literal("modify-resource"),
      entity: entityRefSchema,
      resourceId: z.string(),
      patch: z.object({
        current: z.number().optional(),
        base: z.number().optional(),
        max: z.number().optional(),
      }),
      reason: z.string().optional(),
    }),
  ]) as z.ZodType<EntityPatchOp>;
